'use client'

import { Amplify } from 'aws-amplify'

import outputs from '@/amplify_outputs.json'
import { configureAutoTrack } from 'aws-amplify/analytics'
import { useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { identifyUser } from 'aws-amplify/analytics'
import { fetchUserAttributes, fetchAuthSession } from 'aws-amplify/auth'

// Check only if in production
const isProduction = process.env.NEXT_PUBLIC_NODE_ENV === 'production'

// Configure Amplify to track page views and sessions
const amplifyConfig = {
  ...outputs,
  ...(isProduction && {
    analytics: {
      amazon_pinpoint: {
        app_id: outputs.analytics.amazon_pinpoint.app_id,
        aws_region: outputs.analytics.amazon_pinpoint.aws_region,
      },
    },
  }),
}

// Configure Amplify in the client side
Amplify.configure(amplifyConfig, { ssr: true })

/**
 * Configure Amplify on the client side.
 *
 * This component is responsible for configuring Amplify's analytics module
 * to track page views and sessions.
 *
 * It uses the `usePathname` hook from Next.js to get the current URL path.
 * When the path changes, it configures Amplify to track page views for the
 * current path.
 *
 * It also configures Amplify to track sessions.
 *
 * The component returns null, indicating that it has not rendered any content.
 */
export default function ConfigureAmplifyClientSide() {
  const pathname = usePathname()

  useEffect(() => {
    if (!isProduction) return

    // Fetch the user session when the component mounts
    fetchAuthSession().then((session) => {
      if (session.tokens) {
        fetchUserAttributes().then((attributes) => {
          identifyUser({
            userId: attributes.sub as string,
            userProfile: attributes as Record<string, any>,
          })
        })
      }
    })
  }, [])

  useEffect(() => {
    if (!isProduction) return

    configureAutoTrack({
      type: 'pageView',
      enable: true,
      options: {
        attributes: { page: pathname },
        appType: 'multiPage',
        urlProvider: () => window.location.href,
      },
    })
  }, [pathname])

  // Return null to indicate that the component has not rendered
  return null
}
